import { computed, Injectable, signal } from '@angular/core';
import { TerritoryUser } from '../../interface';

/**
 * Territory map service that handles communication between the consumer and the territory map
 */
@Injectable({
    providedIn: 'root',
})
export class TerritoryMapService {
    /**
     * Unique identifier that gets incremented everytime setTerritoryIntersect is called
     * which then acts as a trigger for the territory map to re-run its effect
     */
    public triggerValidatationId = signal<number>(0);

    /**
     * When consumer clicks the next button, we should run validation if user territory intersect with other territories
     * @private
     */
    private _territoryIntersect = signal<boolean>(false);

    /**
     * Tracks how many markers are currently placed on the map.
     * Updated by the territory map component; consumed by external controls to enable/disable actions.
     */
    public markerCount = signal<number>(0);

    /**
     * Territories to render on the map for the locator variant.
     * Set by the consuming locator page before the map initializes.
     */
    public locatorTerritories = signal<TerritoryUser[]>([]);

    /**
     * When set, the map will fly/fit to these coordinates.
     * Set to null to clear the request.
     */
    public flyToBounds = signal<[number, number][] | null>(null);

    /**
     * Holds the value of the map height, we use this so that external custom controls can have access to the height of the parent as well
     *
     * @remarks
     * This property is especially added so when external custom controls are used, they can access the height of the map
     * so they can say set the height of the custom controls to the same height as the map
     *
     * @public
     */
    public mapHeight = signal<number>(0);

    /**
     * Public and reactive as to whether the user territory intersects with other territories
     * @public
     */
    public territoryIntersect = computed(() => this._territoryIntersect());

    /**
     * Setter for territory intersect
     * @param value
     */
    public setTerritoryIntersect(value: boolean): void {
        this._territoryIntersect.set(value);
    }

    /**
     * Resets the territory intersect to false
     */
    public resetTerritoryIntersect(): void {
        this._territoryIntersect.set(false);
    }

    /**
     * Method to trigger territory validation on territory map
     *
     * Update so that everytime this method is called, it sets new ID so effect in the territory map would re-run
     */
    public triggerTerritoryValidation(): void {
        this.triggerValidatationId.update((id) => id + 1);
    }

    /**
     * Fetch all users territories
     */
    public async getTakenTerritories(): Promise<any> {
        // Mock api call to fetch all the territories (FOR NOW MOCK ONLY - TOBECHANGED | TOBEREMOVED)
        await new Promise((r) => setTimeout(r, 1000));
        return [
            [-1.6259765625, 52.7695392206731],
            [-2.9553222656250004, 51.984880139916655],
            [-1.8347167968750002, 51.34433866059924],
            [0.42846679687500006, 52.119998657638156],
            [-1.6259765625, 52.7695392206731],
        ];
    }
}
